import PrimaryBtn from 'components/btns/PrimaryBtn';
import OutlineBtn from 'components/btns/OutlineBtn';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { useEffect, useState } from 'react';
import AdImagesModal from 'modals/Advertisments/AdImagesModal';
import { useNavigate, useParams } from 'react-router-dom';
import AdPreview from 'components/adPreview/AdPreview';
import ConfirmModal from 'modals/common/ConfirmModal';
import useApiHook from 'hooks/useApiHook';
import { smallTick } from 'helper/helper';
import AdImagesCropper from 'modals/Advertisments/AdImagesCropper';
import LogosUploadModal from 'modals/logos/LogosUploadModal';

const NewAdSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, 'Name is too short!')
    .max(50, 'Name is too long!')
    .matches(/^[a-zA-Z\s]*$/, 'Only letters are allowed')
    .required('Company Name is required')
    .trim(),
  company: Yup.string()
    .min(3, 'Name is too short!')
    .max(50, 'Name is too long!')
    .matches(/^[a-zA-Z\s]*$/, 'Only letters are allowed')
    .required('Campaign Name is required')
    .trim(),
  heading: Yup.string()
    .min(2, 'Heading is too short!')
    .max(20, 'Heading is too long!')
    .required('Ad Heading is required')
    .trim(),
  link: Yup.string()
    .matches(
      /((https):\/\/)(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter correct link!'
    )
    .required('Please enter website')
    .trim(),
  startDateAndTime: Yup.date().required('Start Date is required').nullable(),
  endDateAndTime: Yup.date()
    .required('End Date is required')
    .min(Yup.ref('startDateAndTime'), 'End Date must be after Start Date')
    .nullable(),
});

const initialImagesValues = {
  0: {},
  1: {},
  2: {},
};

const initialValues = {
  title: '',
  company: '',
  heading: '',
  link: '',
  startDateAndTime: null,
  endDateAndTime: null,
};

const AddNewAd = () => {
  const [imagesModalOpen, setImagesModalOpen] = useState(false);
  const [cropperModalOpen, setCropperModalOpen] = useState(false);
  const [logosModalOpen, setLogosModalOpen] = useState(false);
  const [adImages, setAdImages] = useState(initialImagesValues);
  const [logoImages, setLogoImages] = useState(initialImagesValues);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { handleApiCall, isApiLoading } = useApiHook();
  const [isEditing, setIsEditing] = useState(false);
  const [isImageChange, setIsImageChange] = useState(false);
  const [isLogoChange, setIsLogoChange] = useState(false);
  const [cropImage, setCropImage] = useState();
  const navigate = useNavigate();
  const { adId } = useParams();

  useEffect(() => {
    const getAd = async () => {
      if (adId) {
        const result = await handleApiCall({
          method: 'get',
          url: `/admin/ads/one/${adId}`,
        });
        if (result?.status === 200) {
          initialValues.title = result?.data?.ad?.title;
          initialValues.company = result?.data?.ad?.company;
          initialValues.heading = result?.data?.ad?.heading;
          initialValues.link = result?.data?.ad?.link;
          initialValues.startDateAndTime = new Date(
            result?.data?.ad?.startDateAndTime
          );
          initialValues.endDateAndTime = new Date(
            result?.data?.ad?.endDateAndTime
          );
          result?.data?.ad?.images?.forEach((image, index) => {
            setAdImages((prevAdImages) => ({
              ...prevAdImages,
              [index]: { file: null, dataURL: image?.src },
            }));
          });
          result?.data?.ad?.logos?.forEach((logo, index) => {
            setLogoImages((prevLogoImages) => ({
              ...prevLogoImages,
              [index]: { file: null, dataURL: logo?.src },
            }));
          });
        }
      } else {
        initialValues.title = '';
        initialValues.company = '';
        initialValues.heading = '';
        initialValues.link = '';
        initialValues.startDateAndTime = null;
        initialValues.endDateAndTime = null;
      }
    };

    setAdImages(initialImagesValues);
    setLogoImages(initialImagesValues);
    setIsEditing(false);
    setIsImageChange(false);
    setIsLogoChange(false);
    getAd();
  }, []);

  const setAdImagesFromModal = (index, file, dataURL) => {
    if (adId) setIsImageChange(true);
    setAdImages((prevAdImages) => ({
      ...prevAdImages,
      [index]: { file, dataURL },
    }));
  };

  const setLogoImagesFromModal = (index, file, dataURL) => {
    if (adId) setIsLogoChange(true);
    setLogoImages((prevLogoImages) => ({
      ...prevLogoImages,
      [index]: { file, dataURL },
    }));
  };

  const publishAd = async (data) => {
    let missing = 0;

    if (!checkLogoExists()) {
      toast.error('Logo image(s) are missing.');
      missing++;
    }
    if (!checkImagesExist()) {
      toast.error('Ad image(s) are missing.');
      missing++;
    }

    if (missing > 0) return;

    let result;
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      // Append data fields
      formData.append(key, data[key]);
    });

    if (adId) {
      Object.keys(logoImages).forEach((index) => {
        // Append logo images
        formData.append(
          'images',
          logoImages[index].file
            ? logoImages[index].file
            : logoImages[index].dataURL
        );
      });
      Object.keys(adImages).forEach((index) => {
        // Append images
        formData.append(
          'images',
          adImages[index].file ? adImages[index].file : adImages[index].dataURL
        );
      });
      result = await handleApiCall({
        method: 'put',
        url: `admin/ads/edit/${adId}`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      Object.keys(logoImages).forEach((index) => {
        // Append logo images
        formData.append('images', logoImages[index].file);
      });
      Object.keys(adImages).forEach((index) => {
        // Append images
        formData.append('images', adImages[index].file);
      });
      result = await handleApiCall({
        method: 'post',
        url: 'admin/ads/publish',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }

    if (result?.status === 201 || result?.status === 200) {
      toast.success(result?.data?.message);
      handleDiscard();
    }
  };

  function checkImagesExist() {
    if (adImages[0]?.dataURL && adImages[1]?.dataURL && adImages[2]?.dataURL)
      return true;
    return false;
  }

  function checkLogoExists() {
    if (
      logoImages[0]?.dataURL &&
      logoImages[1]?.dataURL &&
      logoImages[2]?.dataURL
    )
      return true;
    return false;
  }

  const handleDiscard = () => {
    setAdImages(initialImagesValues);
    navigate('/ads-management');
  };

  const handleImagesModal = () => {
    setImagesModalOpen(true);
  };

  return (
    <div className='ez__Home w-full relative z-1 p-5 pb-10'>
      <Formik
        initialValues={initialValues}
        validationSchema={NewAdSchema}
        onSubmit={publishAd}
        enableReinitialize={true}
      >
        {({ values, dirty, setFieldValue, handleSubmit }) => (
          <>
            <div
              className={`flex flex-row justify-between items-center ${
                adId && !dirty && !isImageChange && !isLogoChange
                  ? 'mb-3 pt-2'
                  : ''
              }`}
            >
              <h1 className='text-2xl font-semibold text-ezBlack'>Campaigns</h1>
              {adId ? (
                dirty || isImageChange || isLogoChange ? (
                  <div className='flex flex-row'>
                    <OutlineBtn
                      btnType='button'
                      text='Discard'
                      deletebtn
                      isApiLoading={isApiLoading}
                      className={'px-5 py-2 mr-2 mb-3 bg-red-100'}
                      handleEvent={() => setConfirmModalOpen(true)}
                    />
                    <PrimaryBtn
                      btnType='submit'
                      text='Confirm Changes'
                      width
                      isApiLoading={isApiLoading}
                      classNames={'px-5 py-2 mb-3'}
                      handleEvent={handleSubmit}
                    />
                  </div>
                ) : null
              ) : (
                <div className='flex flex-row'>
                  <OutlineBtn
                    btnType='button'
                    text='Discard'
                    deletebtn
                    isApiLoading={isApiLoading}
                    className={'px-5 py-2 mr-2 mb-3 bg-red-100'}
                    handleEvent={() => {
                      dirty
                        ? setConfirmModalOpen(true)
                        : navigate('/ads-management');
                    }}
                  />
                  <PrimaryBtn
                    btnType='submit'
                    text='Publish'
                    width
                    isApiLoading={isApiLoading}
                    classNames={'px-5 py-2 mb-3'}
                    handleEvent={handleSubmit}
                  />
                </div>
              )}
            </div>
            <div className='grid grid-cols-12 gap-3'>
              <div className='col-span-4 card bg-white shadow-sm border p-5'>
                <Form className='ez__Form block relative'>
                  <label className='block mb-1 text-md text-ezBlack'>
                    Company Information
                  </label>
                  <div className='block gap-x-4 gap-y-2'>
                    <div className='block'>
                      <Field
                        type='text'
                        name='title'
                        id='title'
                        placeholder='Company Name'
                        className='w-full block border border-ezGreen rounded-md text-sm text-ezBlack p-3.5 mb-4'
                      />
                      <ErrorMessage
                        name='title'
                        render={(msg) => (
                          <p className='text-xs text-ezRed block mb-4 -mt-3'>
                            {msg}
                          </p>
                        )}
                      />
                    </div>
                    <div className='block'>
                      <Field
                        type='text'
                        name='company'
                        id='company'
                        placeholder='Campaign Name'
                        className='w-full block border border-ezGreen rounded-md text-sm text-ezBlack p-3.5 mb-4'
                      />
                      <ErrorMessage
                        name='company'
                        render={(msg) => (
                          <p className='text-xs text-ezRed block mb-4 -mt-3'>
                            {msg}
                          </p>
                        )}
                      />
                    </div>
                  </div>
                  <div className='block relative'>
                    <label className='mb-1 text-md text-ezBlack'>
                      Company logo
                    </label>
                    <div className='relative w-2/5 mt-1'>
                      <label htmlFor={'upload-logo'} className='block'>
                        <OutlineBtn
                          btnType='button'
                          text='Upload Logo'
                          className={'text-sm px-1 py-1 w-full'}
                          handleEvent={() => setLogosModalOpen(true)}
                        />
                      </label>
                    </div>
                  </div>
                  <ul>
                    <li className='flex items-center pt-2'>
                      <span
                        className={`ez__Svg mx-2 ${
                          logoImages[0]?.dataURL
                            ? 'text-ezGreen'
                            : 'text-ezGray'
                        }`}
                      >
                        {smallTick}
                      </span>
                      Logo Icon <strong className='text-black ml-1'>1:1</strong>
                    </li>
                    <li className='flex items-center'>
                      <span
                        className={`ez__Svg mx-2 ${
                          logoImages[1]?.dataURL
                            ? 'text-ezGreen'
                            : 'text-ezGray'
                        }`}
                      >
                        {smallTick}
                      </span>
                      Dark Background{' '}
                      <strong className='text-black ml-1'>3:1</strong>
                    </li>
                    <li className='flex items-center'>
                      <span
                        className={`ez__Svg mx-2 ${
                          logoImages[2]?.dataURL
                            ? 'text-ezGreen'
                            : 'text-ezGray'
                        }`}
                      >
                        {smallTick}
                      </span>
                      Light Background{' '}
                      <strong className='text-black ml-1'>3:1</strong>
                    </li>
                  </ul>
                  <hr className='my-5 border-t border-gray-300' />
                  <label className='block mb-1 text-md text-ezBlack'>
                    Ad Information
                  </label>
                  <div className='block gap-x-4 gap-y-2'>
                    <div className='block'>
                      <Field
                        type='text'
                        name='heading'
                        id='heading'
                        placeholder='Enter Ad Heading'
                        className='w-full block border border-ezGreen rounded-md text-sm text-ezBlack p-3.5 mb-4'
                      />
                      <ErrorMessage
                        name='heading'
                        render={(msg) => (
                          <p className='text-xs text-ezRed block mb-4 -mt-3'>
                            {msg}
                          </p>
                        )}
                      />
                    </div>
                    <div className='block'>
                      <Field
                        type='text'
                        name='link'
                        id='link'
                        placeholder='Enter Company Link'
                        className='w-full block border border-ezGreen rounded-md text-sm text-ezBlack p-3.5 mb-4'
                      />
                      <p className='text-xs text-ezRed block mb-2 -mt-2'>
                        https://www.example.com
                      </p>
                      <ErrorMessage
                        name='link'
                        render={(msg) => (
                          <p className='text-xs text-ezRed block mb-2 -mt-1'>
                            {msg}
                          </p>
                        )}
                      />
                    </div>
                  </div>
                  <hr className='my-5 border-t border-gray-300' />
                  <label className='block mb-1 text-md text-ezBlack'>
                    {isEditing ? 'Update' : 'Upload'} Ad Images
                  </label>
                  <OutlineBtn
                    btnType='button'
                    text={`${isEditing ? 'Update' : 'Upload'} Images`}
                    className={'text-sm px-3 py-1 mr-4 mb-3'}
                    handleEvent={handleImagesModal}
                  />
                  <div className='flex flex-col text-gray-500'>
                    <h3>Upload 3 images with the following dimensions:</h3>
                    <ul>
                      <li className='flex items-center pt-2'>
                        <span
                          className={`ez__Svg mx-2 ${
                            adImages[0]?.dataURL
                              ? 'text-ezGreen'
                              : 'text-ezGray'
                          }`}
                        >
                          {smallTick}
                        </span>
                        One for{' '}
                        <strong className='text-black ml-1'>16:9</strong>
                      </li>
                      <li className='flex items-center'>
                        <span
                          className={`ez__Svg mx-2 ${
                            adImages[1]?.dataURL
                              ? 'text-ezGreen'
                              : 'text-ezGray'
                          }`}
                        >
                          {smallTick}
                        </span>
                        One for{' '}
                        <strong className='text-black ml-1'>4:15</strong>
                      </li>
                      <li className='flex items-center'>
                        <span
                          className={`ez__Svg mx-2 ${
                            adImages[2]?.dataURL
                              ? 'text-ezGreen'
                              : 'text-ezGray'
                          }`}
                        >
                          {smallTick}
                        </span>
                        One for <strong className='text-black ml-1'>8:1</strong>
                      </li>
                    </ul>
                  </div>
                  <hr className='my-5 border-t border-gray-300' />
                  <label className='block mb-1 text-md text-ezBlack'>
                    Ad Schedule
                  </label>
                  <div className='block gap-x-4 gap-y-2'>
                    <div className='ez__FormInput w-full flex items-center justify-between border border-ezGreen rounded-md p-3 mb-4'>
                      <DatePicker
                        name='startDateAndTime'
                        selected={values?.startDateAndTime}
                        id='startDateAndTime'
                        placeholderText='Select Start Date'
                        minDate={new Date()}
                        onChange={(date) =>
                          setFieldValue('startDateAndTime', date)
                        }
                      />
                      <label
                        htmlFor='startDateAndTime'
                        className='text-ezGreen text-2xl cursor-pointer'
                      >
                        <FaRegCalendarAlt />
                      </label>
                    </div>
                    <ErrorMessage
                      name='startDateAndTime'
                      render={(msg) => (
                        <p className='text-xs text-ezRed block mb-4 -mt-3'>
                          {msg}
                        </p>
                      )}
                    />
                    <div className='ez__FormInput w-full flex items-center justify-between border border-ezGreen rounded-md p-3 mb-4'>
                      <DatePicker
                        name='endDateAndTime'
                        selected={values?.endDateAndTime}
                        id='endDateAndTime'
                        placeholderText='Select End Date'
                        minDate={new Date()}
                        onChange={(date) =>
                          setFieldValue('endDateAndTime', date)
                        }
                      />
                      <label
                        htmlFor='endDateAndTime'
                        className='text-ezGreen text-2xl cursor-pointer'
                      >
                        <FaRegCalendarAlt />
                      </label>
                    </div>
                    <ErrorMessage
                      name='endDateAndTime'
                      render={(msg) => (
                        <p className='text-xs text-ezRed block mb-4 -mt-3'>
                          {msg}
                        </p>
                      )}
                    />
                  </div>
                </Form>
              </div>
              <div className='col-span-8 border'>
                <AdPreview images={adImages} />
              </div>
            </div>
          </>
        )}
      </Formik>
      <AdImagesModal
        ModalHeader={'Upload Images'}
        isViewModal={imagesModalOpen}
        setIsViewModal={setImagesModalOpen}
        adImages={adImages}
        setAdImages={setAdImagesFromModal}
        setCropperModalOpen={setCropperModalOpen}
        setCropImage={setCropImage}
      />
      {cropperModalOpen && (
        <AdImagesCropper
          ModalHeader={'Crop Image'}
          isViewModal={cropperModalOpen}
          setIsViewModal={setCropperModalOpen}
          adImages={adImages}
          setAdImages={setAdImagesFromModal}
          setIsViewPreviousModal={setImagesModalOpen}
          cropImage={cropImage}
          isApiLoading={isApiLoading}
        />
      )}
      <ConfirmModal
        isModal={confirmModalOpen}
        isLoading={false}
        handleCloseModal={() => setConfirmModalOpen(false)}
        title={`Discard ${isEditing ? 'Changes' : 'Information'}?`}
        description={
          isEditing
            ? 'Are you sure? All changes will be discarded.'
            : 'Are you sure you want to discard the current Information? All data in the form will be deleted and needed to refill.'
        }
        handleEvent={handleDiscard}
      />
      <LogosUploadModal
        isDefault={false}
        isViewModal={logosModalOpen}
        setIsViewModal={setLogosModalOpen}
        ModalHeader='Upload Logos'
        logoImages={logoImages}
        setLogoImages={setLogoImagesFromModal}
      />
    </div>
  );
};

export default AddNewAd;
